import { useState } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle,
  ChevronRight,
  FileCode,
  Globe,
  Upload,
  X,
} from 'lucide-react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Spinner,
  Textarea,
  useToast,
} from '@centraoneplus/ui';
import { useImportOpenAPI, useImportYAML, useImportFile } from '@hooks/useIntegrations';

interface ImportWizardProps {
  onClose: () => void;
}

type ImportMethod = 'url' | 'file' | 'yaml';
type Step = 'method' | 'configure' | 'preview' | 'complete';

export function ImportWizard({ onClose }: ImportWizardProps) {
  const toast = useToast();
  const [step, setStep] = useState<Step>('method');
  const [importMethod, setImportMethod] = useState<ImportMethod | null>(null);

  // Form state
  const [url, setUrl] = useState('');
  const [yamlContent, setYamlContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [connectorId, setConnectorId] = useState('');
  const [prefix, setPrefix] = useState('');

  // Import mutations
  const importOpenAPI = useImportOpenAPI();
  const importYAML = useImportYAML();
  const importFile = useImportFile();

  const isLoading =
    importOpenAPI.isPending || importYAML.isPending || importFile.isPending;

  const handleMethodSelect = (method: ImportMethod) => {
    setImportMethod(method);
    setStep('configure');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    try {
      let result;

      if (importMethod === 'url') {
        result = await importOpenAPI.mutateAsync({
          url,
          connector_id: connectorId || undefined,
          prefix: prefix || undefined,
        });
      } else if (importMethod === 'yaml') {
        result = await importYAML.mutateAsync({
          content: yamlContent,
          connector_id: connectorId || undefined,
        });
      } else if (importMethod === 'file' && file) {
        result = await importFile.mutateAsync({
          file,
          options: {
            connector_id: connectorId || undefined,
            prefix: prefix || undefined,
          },
        });
      }

      if (result) {
        setStep('complete');
        toast.success('Import successful', `Imported ${result.imported} capabilities`);
      }
    } catch (err) {
      toast.error('Import failed', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const canProceed = () => {
    if (step === 'configure') {
      if (importMethod === 'url') return url.trim().length > 0;
      if (importMethod === 'yaml') return yamlContent.trim().length > 0;
      if (importMethod === 'file') return file !== null;
    }
    return true;
  };

  const getResult = () => {
    return (
      importOpenAPI.data || importYAML.data || importFile.data
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Import Capabilities" size="lg">
      <div className="p-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {(['method', 'configure', 'complete'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${
                    step === s
                      ? 'bg-blue-600 text-white'
                      : step === 'complete' || (step === 'configure' && i === 0)
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }
                `}
              >
                {step === 'complete' || (step === 'configure' && i === 0) ? (
                  <Check className="h-4 w-4" />
                ) : (
                  i + 1
                )}
              </div>
              {i < 2 && (
                <div
                  className={`w-16 h-0.5 ${
                    step === 'complete' || (step === 'configure' && i === 0)
                      ? 'bg-green-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step: Method Selection */}
        {step === 'method' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">
              Choose Import Method
            </h3>

            <button
              onClick={() => handleMethodSelect('url')}
              className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/20 dark:hover:border-blue-400 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg dark:bg-blue-900/40">
                  <Globe className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Import from URL
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Import capabilities from an OpenAPI/Swagger specification URL
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
            </button>

            <button
              onClick={() => handleMethodSelect('file')}
              className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/20 dark:hover:border-blue-400 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg dark:bg-green-900/40">
                  <Upload className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Upload File
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Upload an OpenAPI spec (JSON/YAML) or custom capability YAML
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
            </button>

            <button
              onClick={() => handleMethodSelect('yaml')}
              className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/20 dark:hover:border-blue-400 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg dark:bg-purple-900/40">
                  <FileCode className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Paste YAML
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Paste capability definitions directly in YAML format
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
            </button>
          </div>
        )}

        {/* Step: Configure */}
        {step === 'configure' && (
          <div className="space-y-6">
            <button
              onClick={() => setStep('method')}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              ← Back to method selection
            </button>

            {importMethod === 'url' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    OpenAPI Specification URL
                  </label>
                  <Input
                    type="url"
                    placeholder="https://api.example.com/openapi.json"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                    Supports OpenAPI 3.x and Swagger 2.0 specifications
                  </p>
                </div>
              </div>
            )}

            {importMethod === 'file' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Select File
                  </label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    {file ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileCode className="h-5 w-5 text-blue-600" />
                        <span className="font-medium">{file.name}</span>
                        <button
                          onClick={() => setFile(null)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2 dark:text-gray-500" />
                        <p className="text-sm text-gray-600 mb-2 dark:text-gray-400">
                          Drop a file here or click to browse
                        </p>
                        <input
                          type="file"
                          accept=".json,.yaml,.yml"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="cursor-pointer text-blue-600 hover:underline text-sm"
                        >
                          Select file
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {importMethod === 'yaml' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    YAML Content
                  </label>
                  <Textarea
                    placeholder={`capabilities:
  - id: my_capability
    name: My Capability
    description: Description here
    parameters:
      - name: param1
        type: string
        required: true`}
                    value={yamlContent}
                    onChange={(e) => setYamlContent(e.target.value)}
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {/* Common Options */}
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Options</h4>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Connector ID (optional)
                </label>
                <Input
                  placeholder="e.g., shopify, salesforce"
                  value={connectorId}
                  onChange={(e) => setConnectorId(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Associate imported capabilities with an Integration Hub connector
                </p>
              </div>

              {importMethod !== 'yaml' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    ID Prefix (optional)
                  </label>
                  <Input
                    placeholder="e.g., shop_"
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                    Add a prefix to all imported capability IDs
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step: Complete */}
        {step === 'complete' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">
              Import Complete
            </h3>

            {getResult() && (
              <div className="text-sm text-gray-600 space-y-1 dark:text-gray-400">
                <p>
                  <strong>{getResult()!.imported}</strong> capabilities imported
                </p>
                {getResult()!.skipped > 0 && (
                  <p>
                    <strong>{getResult()!.skipped}</strong> skipped (already exist)
                  </p>
                )}
                {getResult()!.errors.length > 0 && (
                  <Alert type="warning" className="mt-4 text-left">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5" />
                      <div>
                        <strong>{getResult()!.errors.length} errors:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {getResult()!.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {step === 'complete' ? 'Close' : 'Cancel'}
          </Button>
          {step === 'configure' && (
            <Button
              onClick={handleImport}
              disabled={!canProceed() || isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Importing...
                </>
              ) : (
                'Import'
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
